import { action, computed, observable } from 'mobx';
import httpFacade from 'http/httpFacade';
import EmailNewModalForm from 'stores/Forms/Email/EmailNewModalForm';
import { EmailUserGroupsResponse } from 'http/Api/Email/types';
import { PromiseService } from 'services/PromiseService';

import { IGroupTag } from 'components/Modals/TagsSelectModal/TagsSelectModal';

class EmailNewModalStore {
  @observable form = new EmailNewModalForm();
  @observable userGroupTags: IGroupTag[] = [];
  @observable selectedUserGroupTags: IGroupTag[] = [];

  @observable private _footer = '';
  @observable private _promiseService = new PromiseService();

  @action.bound
  async init() {
    const userGroupsReq = httpFacade.email.fetchUserGroups();
    const footerReq = httpFacade.email.fetchEmailFooter();

    const [userGroups, footer] = await this._promiseService.add(() =>
      Promise.all([userGroupsReq, footerReq]),
    );

    this.userGroupTags = this._getUserGroupTags(userGroups.data);
    this._footer = footer.data;
  }

  @action.bound
  selectUserGroupTags(tags: IGroupTag[]) {
    this.selectedUserGroupTags = tags;
  }

  @action.bound
  removeUserGroupTag(tagId?: string) {
    this.selectedUserGroupTags = this.selectedUserGroupTags.filter(
      ({ id, groupId }) => id !== tagId && groupId !== tagId,
    );
  }

  @action.bound
  resetUserGroupTags() {
    this.selectedUserGroupTags = [];
  }

  submit = async (onSubmit?: () => void) => {
    if (!this.form.validate() || this.isSubmitDisabled) return;

    await this._promiseService.add(() =>
      httpFacade.email.sendEmail({
        to: this.to,
        subject: this.form.subject,
        message: this._getMessage(
          this.form.message,
          this._footer,
          this.form.contactEmail,
        ),
      }),
    );

    onSubmit?.();
  };

  private _getUserGroupTags(groups: EmailUserGroupsResponse) {
    const groupTags: IGroupTag[] = [];

    for (const group in groups) {
      if (!groups.hasOwnProperty(group)) continue;

      groupTags.push({ id: group, title: group, isGroup: true });

      for (const user of groups[group]) {
        groupTags.push({ id: user.id, title: user.email, groupId: group });
      }
    }

    return groupTags;
  }

  private _getMessage(message: string, footer: string, contactEmail: string) {
    const replaceValue = `<a href="mailto:${contactEmail}">${contactEmail}</a>`;
    const footerText = footer.replace('Ihre E-Mail', replaceValue);

    return `${message}<br><br><br>${footerText}`;
  }

  @computed
  get to() {
    const emails = this.selectedUserGroupTags
      .filter(({ isGroup }) => !isGroup)
      .map(({ title }) => title);

    return emails.join(', ');
  }

  @computed
  get isSubmitDisabled() {
    return this.form.invalidFields.length || !this.selectedUserGroupTags.length;
  }

  @computed
  get isLoading() {
    return this._promiseService.isPending || !this._promiseService.isCompleted;
  }
}

export default EmailNewModalStore;
